
import { Mail, Phone, User, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatDate } from "@/utils/dateUtils";
import { Badge } from "@/components/ui/badge";
import { Client } from "@/types/types";

interface ClientContactInfoProps {
  client: Client;
}

export function ClientContactInfo({ client }: ClientContactInfoProps) {
  const { t } = useTranslation("clients");

  // Le test portait sur `typeof lastVisit === 'string'` alors que le store y
  // mettait un objet `Date` : « Dernière visite : Jamais » s'affichait en
  // permanence. On accepte les deux formes et on écarte les dates invalides,
  // `formatDate` levant sur `new Date("")`.
  const lastVisitDate = (() => {
    const raw = client.lastVisit ?? client.lastBookingAt;
    if (!raw) return null;
    const d = raw instanceof Date ? raw : new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  })();

  return (
    <div className="space-y-2">
      <div className="flex items-center text-sm">
        <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
        <span>{client.email || "—"}</span>
      </div>
      <div className="flex items-center text-sm">
        <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
        <span>{client.phone || "—"}</span>
      </div>
      <div className="flex items-center text-sm">
        <User className="h-4 w-4 mr-2 text-muted-foreground" />
        <span>{t("bornOn")} {client.birthDate && formatDate(client.birthDate)}</span>
      </div>
      <div className="flex items-start text-sm">
        <CheckCircle2 className="h-4 w-4 mr-2 text-muted-foreground shrink-0 mt-0.5" />
        <span>{t("lastVisit")}: {lastVisitDate ? formatDate(lastVisitDate.toISOString()) : t("never")}</span>
      </div>
      {client.clothingSize && (
        <div className="flex items-center text-sm">
          <span className="mr-2 text-muted-foreground text-xs uppercase">Taille</span>
          <Badge variant="secondary">{client.clothingSize}</Badge>
        </div>
      )}
    </div>
  );
}
